/**
 * Category links for a browse container.
 *
 * The 19 `?preset=` category pages were, until now, orphans: no page on any of
 * the three archives linked to one (measured 2026-07-22 — the only internal
 * links a crawler could see were the seven header/footer destinations), and
 * they were not in the sitemap either. A URL nothing links to and nothing
 * declares does not exist as far as a crawler is concerned, however good the
 * page behind it is.
 *
 * These are real landing pages ("Current Standards", "Archived Directives",
 * "Recently Rescinded Delegations") that a person browsing an archive wants
 * anyway, so this is navigation first and crawl surface second. Rendered as
 * ordinary `<a href>` (react-router `Link`), never as onClick handlers — an
 * href is the only kind of link a crawler follows.
 *
 * GROUPS. A preset carrying `group` (the 40 directives series) is not in the
 * strip. Until 2026-09-27 each group rendered here as a second wall of chips,
 * above the results; the visitor report was that it was bad UI and that
 * series should be a facet. It is now: picking a series in the Series facet
 * goes to its page (LeftColumnFacets). `BrowseGroups` below keeps the same
 * set as plain links in a closed disclosure UNDER the results, for scanning
 * and because they are BL-0087's crawl path: a closed `<details>` is still in
 * the DOM, Google follows any `<a href>`, and Google's spam policies name
 * accordion-style show/hide content as not hidden text. Moving it below the
 * results gives the top of the page, where most viewing time goes (NN/g,
 * scrolling and attention), back to the results.
 *
 * Every group member is linked regardless of how many documents it holds.
 * Whether it is offered to search engines is a separate decision made from
 * the live count by `Preset.minTotal`; the link is what shortens the crawl
 * path to the documents inside, and it costs nothing to a reader.
 */
import React from 'react';
import { Link } from 'react-router-dom';

import { presetsFor } from '../../presets';
import { presetHref } from './browseState';

/** Heading for a grouped set of presets, keyed by `Preset.group`. */
const GROUP_LABEL = {
  series: 'Browse by series',
};

/** Label for the unfiltered container, per browse family. */
const ALL_LABEL = {
  documents: 'All Documents',
  delegations: 'All Delegations',
  glossary: 'Full Glossary',
  people: 'All People',
};

/** Registry order is nav order, so grouping preserves it within each group. */
export const groupPresets = (presets = []) => {
  const groups = [];
  const index = {};
  presets.forEach(({ id, label, group }) => {
    if (!group) return;
    if (!index[group]) {
      index[group] = { group, entries: [] };
      groups.push(index[group]);
    }
    index[group].entries.push({ id, label });
  });
  return groups;
};

const BrowseNav = ({ site, family, pathname, activePreset }) => {
  const presets = presetsFor(site?.id, family);
  if (!presets.length) return null;

  const entries = [
    { id: null, label: ALL_LABEL[family] || 'All' },
    ...presets
      .filter(({ group }) => !group)
      .map(({ id, label }) => ({ id, label })),
  ];
  const renderEntry = ({ id, label }) => {
    const isActive = (id || null) === (activePreset || null);
    return (
      <li key={id || '_all'} className="pp-browse-nav__entry">
        {isActive ? (
          <span className="pp-browse-nav__item is-active" aria-current="page">
            {label}
          </span>
        ) : (
          <Link className="pp-browse-nav__item" to={presetHref(pathname, id)}>
            {label}
          </Link>
        )}
      </li>
    );
  };

  return (
    <nav className="pp-browse-nav" aria-label="Browse categories">
      <ul className="pp-browse-nav__list">{entries.map(renderEntry)}</ul>
    </nav>
  );
};

/**
 * Grouped presets as a closed disclosure of plain links, rendered after the
 * pager (see the header). Native `<details>`/`<summary>`: keyboard and screen
 * reader support come with the element, and the links are in the server HTML
 * whether it is open or not.
 */
export const BrowseGroups = ({ site, family, pathname, activePreset }) => {
  const groups = groupPresets(presetsFor(site?.id, family));
  if (!groups.length) return null;
  return groups.map(({ group, entries }) => {
    const label = GROUP_LABEL[group] || group;
    return (
      <nav className="pp-browse-groups" aria-label={label} key={group}>
        <details className="pp-browse-groups__details">
          <summary className="pp-browse-groups__summary">
            {label} ({entries.length})
          </summary>
          <ul className="pp-browse-groups__list">
            {entries.map(({ id, label: entryLabel }) => (
              <li key={id} className="pp-browse-groups__entry">
                {id === activePreset ? (
                  <span
                    className="pp-browse-groups__link is-active"
                    aria-current="page"
                  >
                    {entryLabel}
                  </span>
                ) : (
                  <Link
                    className="pp-browse-groups__link"
                    to={presetHref(pathname, id)}
                  >
                    {entryLabel}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </details>
      </nav>
    );
  });
};

export default BrowseNav;
